'use client';

import { useQuery } from '@northladder/services';

// Find ways of reducing this path since we cannot index exports as they would
// be undefined at runtime.
import { IFetchAudioClipResponse } from '../../../../app/api/audio/get-audio-clip';
import { IFinalSpeechToTextResponseError } from '../../../../app/api/google/speech-to-text';

interface IFetchAudioClipFromS3BucketAPIOptions {
  /**
   * The ID of the evaluation to be processed.
   */
  evaluationId: string;
  /**
   * The baseURL of the endpoint that will be used to validate the request session
   * since this is dynamic depending on the tenant config env.
   */
  validationBaseURL: string;
}

/**
 * This will call an API that randomly retrieves and an audio clip config and uses
 * it to get the matching words and an audio clip file from S3 bucket that will
 * later be played back in the browser.
 */
export async function fetchAudioClipAPI({
  evaluationId,
  validationBaseURL,
}: IFetchAudioClipFromS3BucketAPIOptions) {
  /**
   * The endpoint to fetch the audio clip from the s3 bucket
   */
  const AUDIO_CLIP_URL = '/api/audio/get-audio-clip';

  const response = await fetch(AUDIO_CLIP_URL, {
    method: 'POST',
    body: JSON.stringify({
      evaluationId,
      validationBaseURL,
    }),
  });

  const responseData = (await response.json()) as IFetchAudioClipResponse;

  if (!responseData.success) {
    throw responseData;
  }

  return {
    base64Audio: responseData.base64Audio,
    words: responseData.words,
  };
}

export interface ISoundClipData {
  base64Audio: string;
  words: string[];
}

interface IUseSoundClipQueryOptions {
  evaluationId: string;
  validationBaseURL: string;
  onError: (error: IFinalSpeechToTextResponseError) => void;
  onSuccess: (data: ISoundClipData) => Promise<void>;
}

/**
 * This is the query key for the sound clip to be added to the query cache.
 */
const SOUND_CLIP_QUERY_KEY = 'sound-clip-query';

/**
 * This custom query hook helps in fetching the sound clip from the S3 bucket.
 */
export const useSoundClipQuery = (options: IUseSoundClipQueryOptions) => {
  const { evaluationId, validationBaseURL } = options;

  return useQuery({
    queryKey: [SOUND_CLIP_QUERY_KEY, evaluationId, validationBaseURL],
    queryFn: () =>
      fetchAudioClipAPI({
        evaluationId,
        validationBaseURL,
      }),
    networkMode: 'always',
    enabled: true,
    retry: false,
    // cacheTime: 120000, // Garbage Collect after 2 minutes
    // @team - Revise this as to why I changed the names here.
    // https://tanstack.com/query/latest/docs/framework/react/guides/migrating-to-v5#rename-cachetime-to-gctime
    gcTime: 1000, // Garbage Collect after 2 minutes
    // staleTime: 60000, // Refetch after 1 minute
    staleTime: 4000, // Refetch after 1 minute
  });
};
